import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import {
  READER_DEFAULTS,
  READER_LEADING,
  READER_SIZE_REM,
} from "@/hooks/useReaderSettings";
import type { ReaderSettingsValue } from "@/hooks/useReaderSettings";

interface ReadingTextProps {
  html: string;
  onWordClick: (
    word: string,
    sentence: string,
    occurrence: number,
    rect: DOMRect,
  ) => void;
  trackedLemmas?: Set<string>;
  /** Passage size/leading; defaults to the reader defaults when omitted. */
  settings?: ReaderSettingsValue;
  /**
   * True while a word lookup is open. The word it was opened from stays marked until
   * this goes false, so a reader whose eyes are on the panel can find their place
   * again when they look back.
   */
  lookupOpen?: boolean;
}

/**
 * Renders reading HTML with every word wrapped in a clickable span.
 * Extracts the enclosing sentence (parent element text) on click.
 *
 * Emphasis keeps its italic but not a colour: `prose-em:text-em` painted every
 * emphasised phrase emerald on top of the slant, which read as shouting rather than
 * as emphasis.
 */
export function ReadingText({
  html,
  onWordClick,
  trackedLemmas,
  settings = READER_DEFAULTS,
  lookupOpen = false,
}: ReadingTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * The mark is held as the word's position in the passage rather than as a class on
   * the node: tracking a word re-renders the whole passage from its HTML, which
   * replaces every node and would silently drop a class written onto the old one.
   */
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const readerStyle = {
    "--reader-size": String(READER_SIZE_REM[settings.size]),
    "--reader-leading": String(READER_LEADING[settings.leading]),
  } as CSSProperties;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.dataset.word) return;

      const word = target.dataset.word;
      const parentEl = target.closest("[data-sentence]") as HTMLElement | null;
      const sentence = parentEl?.textContent?.trim() ?? word;
      const occurrence = sentenceOccurrence(target, word);
      const rect = target.getBoundingClientRect();

      setActiveIndex(Number(target.dataset.idx));
      onWordClick(word, sentence, occurrence, rect);
    },
    [onWordClick],
  );

  const processedHtml = useMemo(
    () => wrapWordsInHtml(html, trackedLemmas ?? new Set()),
    [html, trackedLemmas],
  );

  /**
   * Held stable so React skips the innerHTML write when nothing about the passage
   * changed. A fresh `{ __html }` object each render makes React rewrite the whole
   * passage — re-parsing it and replacing every node — on any parent state change.
   */
  const markup = useMemo(() => ({ __html: processedHtml }), [processedHtml]);

  // Re-applied after every render of the passage, for the same reason.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container
      .querySelectorAll<HTMLElement>(".word-token.is-active")
      .forEach((el) => el.classList.remove("is-active"));

    if (!lookupOpen || activeIndex === null) return;
    container
      .querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`)
      ?.classList.add("is-active");
  }, [processedHtml, activeIndex, lookupOpen]);

  return (
    <div
      ref={containerRef}
      className="reading-content prose font-serif prose-headings:text-heading prose-headings:font-display prose-p:text-body prose-strong:text-heading prose-a:text-accent max-w-[70ch] font-light select-none"
      style={readerStyle}
      onClick={handleClick}
      dangerouslySetInnerHTML={markup}
    />
  );
}

export const WORD_PATTERN = /(\S+)/g;

export const BLOCK_TAGS = new Set([
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "LI",
  "BLOCKQUOTE",
  "DIV",
  "TD",
  "TH",
]);

/**
 * Parses HTML, wraps each word in text nodes with a clickable span,
 * and marks block-level elements as sentence containers.
 */
export function wrapWordsInHtml(html: string, tracked: Set<string>): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  processNode(doc.body, tracked, { n: 0 });

  return doc.body.innerHTML;
}

function processNode(node: Node, tracked: Set<string>, counter: { n: number }): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? "";
    if (!text.trim()) return;

    const wrapped = text.replace(WORD_PATTERN, (match) => {
      const clean = stripPunctuation(match);
      if (!clean) return match;
      const isTracked = tracked.has(clean.toLowerCase());
      const cls = isTracked ? "word-token tracked" : "word-token";
      // Position in the passage — stable across re-renders of the same text, which
      // is what lets the active mark survive one.
      const idx = counter.n++;
      return `<span class="${cls}" data-idx="${idx}" data-word="${escapeAttr(clean)}">${match}</span>`;
    });

    if (wrapped !== text) {
      const template = document.createElement("template");
      template.innerHTML = wrapped;
      const parent = node.parentNode;
      if (parent) {
        while (template.content.firstChild) {
          parent.insertBefore(template.content.firstChild, node);
        }
        parent.removeChild(node);
      }
    }
    return;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    if (BLOCK_TAGS.has(el.tagName)) {
      el.setAttribute("data-sentence", "true");
    }

    const children = Array.from(node.childNodes);
    for (const child of children) {
      processNode(child, tracked, counter);
    }
  }
}

export function stripPunctuation(word: string): string {
  return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
}

function sentenceOccurrence(target: HTMLElement, word: string): number {
  const parentEl = target.closest("[data-sentence]") as HTMLElement | null;
  if (!parentEl) return 0;

  const targetLower = word.toLowerCase();
  const matches = Array.from(
    parentEl.querySelectorAll<HTMLElement>("[data-word]"),
  ).filter((el) => el.dataset.word?.toLowerCase() === targetLower);

  const index = matches.indexOf(target);
  return index >= 0 ? index : 0;
}

function escapeAttr(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
